import { useEffect, useRef, useState } from 'react';
import { Client } from '@stomp/stompjs';
import SockJS from 'sockjs-client';
import {guardURL} from '../../../../Apis/Constants';
import {getDateFormat, getDateLong, postSessionStorageLoader} from '../../../../Apis/functions';
import {STATUS_MODEL} from '../../../../enums/statusEnum';

/**
 *
 * @Author: Cecilia López Martínez
 * @JobTitle : Full Stack Developer - IBL
 * @Email : clopez@ibl.mx
 *
 *  @param model item: model of header section
 *  @param boolean isOnline: Variable that indicates whether the WebSocket is open
 *  @param useState setIsOnline: Variable to update the WebSocket state when it opens or closes
 *  @param event setScheduleOrders: State to update the scheduled orders
 *  @param event setServerOrders: State to update the served orders
 *
 */
const useWebSocket = ({ item, isOnline, setIsOnline, setScheduleOrders, setServerOrders,  setPendingOrders}) => {
    const [orders, setOrders] = useState([]);
    const ordersRef = useRef([]);

    const clientRef = useRef(null); // Referencia para el cliente STOMP
    const subscriptionsRef = useRef([]); // Para guardar las suscripciones activas

    /**
     * Function to connect to WebSocket
     * @param cafeteriaId
     */
    const connectWebSocket = (cafeteriaId) => {
        if(!cafeteriaId){
            setOrders([]);
            ordersRef.current = [];
            setIsOnline(false);
            return;
        }
        const servingDate = getDateLong(item?.date);
        const topicDate = getDateFormat(item?.date, false,  "YYYY-MM-DD");

        const socket = () => new SockJS(guardURL);
        const client = new Client({
            webSocketFactory: socket,
            debug: () => {},
            reconnectDelay: 5000,
            onConnect: () => {
                setIsOnline(true);
                subscriptionsRef.current.forEach(sub => sub.unsubscribe());
                subscriptionsRef.current = [];

                const sub1 = client.subscribe(`/topic/kitchen/${cafeteriaId}/${topicDate}`, message => {
                    const { data } = JSON.parse(message.body);
                    if (data) {
                        setScheduleOrders((prev) => prev + (data.scheduledOrders ?? 0));
                        handleSetOrders(data, ordersRef.current);
                    }
                });

                const sub2 = client.subscribe(`/topic/kitchen/history/${cafeteriaId}/${topicDate}`, message => {
                    const { data } = JSON.parse(message.body);
                    if (data) {

                        setScheduleOrders(data.scheduledOrders ?? 0);
                        setServerOrders(data.serverOrders ?? 0);
                        setPendingOrders(data.orders?.length ?? 0);
                        setOrders(data.orders ?? []);
                        ordersRef.current = data.orders ?? [];
                        postSessionStorageLoader(false);
                    }
                });
                subscriptionsRef.current = [sub1, sub2];
                const dto = {
                    servingDate,
                    cafeteriaId,
                };
                client.publish({
                    destination: `/app/kitchen/history`,
                    body: JSON.stringify(dto)
                });
            },
            onStompError: (frame) => {
                setIsOnline(false);
            },
            onWebSocketClose: () => {
                setIsOnline(false);
            },
            onWebSocketError: () => {
                setIsOnline(false);
            },
        });
        client.activate();
        clientRef.current = client;
    }

    /**
     * Connect to WebSocket
     */
    useEffect(() => {
        const cafeteriaId = item?.cafeteria?.id;
        setOrders([]);
        ordersRef.current = [];
        if(cafeteriaId){
            connectWebSocket(cafeteriaId);
        }
        return () => {
            subscriptionsRef.current.forEach(sub => sub.unsubscribe());
            subscriptionsRef.current = [];
            if (clientRef.current) {
                clientRef.current.deactivate();
                clientRef.current = null;
            }
        };
    }, [item?.cafeteria?.id, item?.date]);

    /**
     * function to handle and set the values returned from the WebSocket
     * @param data
     */
    const handleSetOrders = (data, current) => {
        if (!data?.orders?.length) return;
        const incomingOrders = data.orders;
        let updatedOrders = [...current];
        incomingOrders.forEach((item) => {
           if (item.statusId === STATUS_MODEL.FOODIE_USER_SELECTION_BOOKED) {
               setScheduleOrders(prev => prev + 1);
           }

            if (item.statusId === STATUS_MODEL.FOODIE_USER_SELECTION_CHECK_IN) {

                const exists = updatedOrders.some(order => order.id === item.id);
                if (!exists) {
                     setScheduleOrders(prev => Math.max(prev - 1, 0));
                    setPendingOrders(prev => prev + 1 )
                    updatedOrders.push(item);
                }
            }

            if (item.statusId === STATUS_MODEL.FOODIE_USER_SELECTION_SERVED) {

                setServerOrders(prev => prev + 1);
                setPendingOrders(prev => Math.max(prev - 1, 0));
                updatedOrders = updatedOrders.filter(order => order.id !== item.id);
            }
        });
        setOrders([...updatedOrders]);
        ordersRef.current = [...updatedOrders];
    };

    /**
     * Function to handle WebSocket reconnection on failure
     */
    const reconnectWebSocket = () => {
        if (!isOnline && !clientRef.current?.connected) {
            connectWebSocket(item?.cafeteria?.id);
        }
    };

    return { orders, setOrders, reconnectWebSocket };
};

export default useWebSocket
